/* ==========================
 * Combo Save - Web Component
 * 可复用的组合促销组件,支持多实例
 * ========================== */

/* --------------------------
 * 数据层(Data Layer)
 * -------------------------- */

// ① 配置服务:统一管理所有配置源
class ConfigService {
  constructor(componentConfig = {}) {
    this.componentConfig = componentConfig;
    this.config = this._loadConfig();
    this._validateConfig();
  }

  _loadConfig() {
    const defaults = {
      product1Title: "Flagship Bumblebee Auto-Converting Robot",
      product2Title: "Flagship Megatron Auto-Converting Robot",
      collectionHandle: "",
      limit: 100,
      tiers: "",
      tiersTitle: "",
      excludeTitles: "",
      // 新增:商品配置规则
      productRules: {
        variantExpandTargets: [
          "Toy Story Buzz Lightyear Robot",
          "Mini Robot - Toy Story Set",
        ], // 需要展开变体的商品
        variantSelectableTargets: ["Interstellar Scout K1 Programming Robot"], // 可选变体的商品
      },
      // 新增:颜色映射配置
      colorMapping: {
        white: "white",
        blue: "blue",
        red: "red",
        black: "black",
      },
      // 新增:排除商品的额外变体映射(用于自动添加赠品)
      excludedProductBonusVariants: {
        "flagship megatron auto-converting robot": "",
        "flagship grimlock auto-converting robot (collector's edition)": "",
        "flagship bumblebee auto-converting robot": "",
      },
      // 变体价格调整规则:
      // 当两个选中变体的 ID 均不在 excludedVariantIds 列表中时,
      // data-total 和 data-save 额外各减 deductionCents 分。
      // 如需禁用此功能，可在 window.BF_CONFIG 中设置 variantPriceAdjustment: null
      // variantPriceAdjustment: {
      //   excludedVariantIds: [
      //     52002627158229, 52002627190997, 52046698021077, 52046698053845,
      //     52046698086613, 52046698119381, 52046698152149, 52002637775061,
      //   ],
      //   deductionCents: 2000,
      // },
      variantPriceAdjustment: null,
    };

    // 优先级: 组件属性 > 全局配置 > 默认值
    const globalConfig = window.BF_CONFIG || {};
    return Object.assign({}, defaults, globalConfig, this.componentConfig);
  }

  _validateConfig() {
    if (!this.config.collectionHandle) {
      console.warn("【配置警告】未设置 collectionHandle");
    }
  }

  get(key, defaultValue = null) {
    return this.config[key] ?? defaultValue;
  }

  // 检查商品是否需要展开变体
  shouldExpandVariants(productTitle) {
    return this.config.productRules.variantExpandTargets.includes(productTitle);
  }

  // 检查商品是否显示变体选择器
  shouldShowVariantSelector(productTitle) {
    return this.config.productRules.variantSelectableTargets.includes(
      productTitle,
    );
  }

  // 获取颜色CSS类名
  getColorClass(variantTitle) {
    const lowerTitle = (variantTitle || "").toLowerCase();
    const colorMapping = this.config.colorMapping;

    for (const [key, value] of Object.entries(colorMapping)) {
      if (lowerTitle.includes(key)) {
        return value;
      }
    }
    return "white"; // 默认颜色
  }

  // 获取排除商品的额外赠品变体ID
  getBonusVariantId(productTitle) {
    const lowerTitle = (productTitle || "").toLowerCase();
    const bonusMapping = this.config.excludedProductBonusVariants;
    return bonusMapping[lowerTitle] || null;
  }
}

// ② 商品服务:API调用与缓存管理
class ProductService {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map(); // 防止重复请求
  }

  async fetchProduct(handle) {
    if (this.cache.has(handle)) {
      return this.cache.get(handle);
    }

    // 防止并发重复请求
    if (this.pendingRequests.has(handle)) {
      return this.pendingRequests.get(handle);
    }

    const request = this._doFetchProduct(handle);
    this.pendingRequests.set(handle, request);

    try {
      const data = await request;
      this.cache.set(handle, data);
      return data;
    } finally {
      this.pendingRequests.delete(handle);
    }
  }

  async _doFetchProduct(handle) {
    const url = `/products/${handle}.js`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Product fetch failed: ${handle}`);
    return await res.json();
  }

  async fetchCollection(handle, limit = 100) {
    const url = `/collections/${handle}/products.json?limit=${limit}`;
    const res = await fetch(url, {
      headers: { Accept: "application/json" },
    });

    if (!res.ok) return [];

    const json = await res.json();
    return (json.products || []).map((p) => ({
      handle: p.handle,
      title: p.title,
      images: (p.images || []).map((i) => i.src),
      featured_image: p.image?.src,
      variants: p.variants,
    }));
  }

  // 批量预加载(性能优化)
  async preloadProducts(handles) {
    return Promise.allSettled(handles.map((h) => this.fetchProduct(h)));
  }

  clear() {
    this.cache.clear();
    this.pendingRequests.clear();
  }
}

// ③ 状态存储:集中式状态管理
class StateStore {
  constructor() {
    this.state = {
      products: [],
      selections: {
        1: { product: null, variant: null },
        2: { product: null, variant: null },
      },
    };
    this.listeners = new Set();
  }

  // 订阅状态变化
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  _notify(type, payload) {
    this.listeners.forEach((cb) => cb({ type, payload }));
  }

  setProducts(products) {
    this.state.products = products;
    this._notify("PRODUCTS_LOADED", products);
  }

  getProducts() {
    return this.state.products;
  }

  selectProduct(slot, product, variant = null) {
    this.state.selections[slot] = { product, variant };
    this._notify("PRODUCT_SELECTED", { slot, product, variant });
  }

  getSelection(slot) {
    return this.state.selections[slot];
  }

  getAllSelections() {
    return [this.state.selections[1], this.state.selections[2]];
  }

  updateVariant(slot, variant) {
    if (this.state.selections[slot]) {
      this.state.selections[slot].variant = variant;
      this._notify("VARIANT_CHANGED", { slot, variant });
    }
  }

  clear() {
    this.listeners.clear();
    this.state = {
      products: [],
      selections: {
        1: { product: null, variant: null },
        2: { product: null, variant: null },
      },
    };
  }
}

/* --------------------------
 * 业务逻辑层(Business Layer)
 * -------------------------- */

// ④ 阶梯满减引擎
class DiscountEngine {
  constructor(configService, stateStore) {
    this.config = configService;
    this.store = stateStore;
    this.tiers = [];
    this.excludedTitles = new Set();
  }

  parseTiers(tiersConfig) {
    this.tiers = (tiersConfig || "")
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map(this._parseTierLine)
      .filter((t) => t && t.off && t.over)
      .sort((a, b) => a.over - b.over);
  }

  _parseTierLine(line) {
    const [off, over] = line
      .split("|")
      .map((v) => parseFloat(v.replace(/,/g, "")) * 100);
    return { off, over };
  }

  setExcludedTitles(titles) {
    this.excludedTitles = new Set(
      (Array.isArray(titles) ? titles : titles.split(/\n+/))
        .map((t) => (t || "").trim().toLowerCase())
        .filter(Boolean),
    );
  }

  calculateTotals() {
    const selections = this.store.getAllSelections();

    return selections.reduce(
      (acc, { product, variant }) => {
        if (!product) return acc;

        const effectiveVariant =
          variant || this._getDefaultVariant(product.variants);
        const price = effectiveVariant?.price ?? product.price ?? 0;
        const compare =
          effectiveVariant?.compare_at_price ??
          product.compare_at_price ??
          price;

        acc.price += price;
        acc.compare += compare;
        return acc;
      },
      { price: 0, compare: 0 },
    );
  }

  _getDefaultVariant(variants) {
    return variants?.find((v) => v.available) || variants?.[0];
  }

  calculateTierEligibleAmount(totalPrice) {
    const selections = this.store.getAllSelections();

    const excludedPrice = selections.reduce((sum, { product, variant }) => {
      if (!product) return sum;

      const title = (product.title || "").toLowerCase();
      if (!this.excludedTitles.has(title)) return sum;

      const effectiveVariant =
        variant || this._getDefaultVariant(product.variants);
      const price = effectiveVariant?.price ?? product.price ?? 0;
      return sum + price;
    }, 0);

    return Math.max(0, totalPrice - excludedPrice);
  }

  getAchievedTier(amount) {
    let achieved = null;
    for (const tier of this.tiers) {
      if (amount >= tier.over) achieved = tier;
      else break;
    }
    return achieved;
  }

  getNextTier(amount) {
    return this.tiers.find((t) => t.over > amount);
  }

  getTiers() {
    return this.tiers;
  }

  /**
   * 计算变体价格调整扣减额（单位：分）
   * 规则：两个产品均已选中，且选中的变体 ID 均不在 excludedVariantIds 中时，返回 deductionCents。
   * 如需移除此功能：将 variantPriceAdjustment 配置设为 null 即可，或删除此方法及调用处。
   */
  calculateVariantDeduction() {
    const adjustment = this.config?.get("variantPriceAdjustment");
    if (!adjustment || !adjustment.deductionCents) return 0;

    const selections = this.store.getAllSelections();

    // 两个插槽均已选产品才触发
    const bothSelected = selections.every(({ product }) => product !== null);
    if (!bothSelected) return 0;

    // 两个变体都在豁免列表中才不扣减，只要有一个不在列表则扣减
    const allExcluded = selections.every(
      ({ variant }) =>
        variant && adjustment.excludedVariantIds.includes(variant.id),
    );

    return allExcluded ? 0 : adjustment.deductionCents;
  }
}

// ⑤ 商品选择协调器
class ProductSelector {
  constructor(productService, stateStore, configService, discountEngine) {
    this.productService = productService;
    this.store = stateStore;
    this.config = configService;
    this.discount = discountEngine;
  }

  async selectProduct(slot, handle, variantId = null) {
    const product = await this.productService.fetchProduct(handle);

    let variant = null;
    if (variantId) {
      variant = product.variants?.find((v) => v.id === variantId);
    }

    if (!variant && product.variants?.length > 0) {
      variant =
        product.variants.find((v) => v.available) || product.variants[0];
    }

    this.store.selectProduct(slot, product, variant);
    return { product, variant };
  }

  changeVariant(slot, variantId) {
    const { product } = this.store.getSelection(slot);
    if (!product) return;

    const variant = product.variants?.find((v) => v.id === variantId);
    if (variant) {
      this.store.updateVariant(slot, variant);
    }
  }

  async loadProductList(collectionHandle, limit) {
    const products = await this.productService.fetchCollection(
      collectionHandle,
      limit,
    );
    this.store.setProducts(products);

    // 预加载需要展开变体的商品
    const targetsToPreload = products
      .filter((p) => this.config.shouldExpandVariants(p.title))
      .map((p) => p.handle);

    if (targetsToPreload.length > 0) {
      await this.productService.preloadProducts(targetsToPreload);
    }

    return products;
  }

  getCheckoutItems() {
    const selections = this.store.getAllSelections();
    const items = [];

    // 添加用户选择的商品变体
    selections.forEach(({ product, variant }) => {
      if (variant?.id) {
        items.push({ id: variant.id, quantity: 1 });

        // 检查是否需要添加额外的赠品变体
        if (product?.title) {
          const bonusVariantId = this.config.getBonusVariantId(product.title);
          if (bonusVariantId) {
            items.push({ id: bonusVariantId, quantity: 1 });
          }
        }
      }
    });

    return items;
  }
}

// ⑥ 结账协调器
class CheckoutCoordinator {
  constructor(productSelector) {
    this.selector = productSelector;
  }

  async loadGiftBundleHelpers() {
    try {
      return await import("gift-config");
    } catch (error) {
      console.warn("gift-config load failed, checkout without bundle gifts", error);
      return null;
    }
  }

  async checkout() {
    let items = this.selector.getCheckoutItems();
    if (items.length < 2) throw new Error("请先选择两个产品");

    // 按主品 variant 查 gift-config：bundleAdd + 有货则一并加购
    const giftHelpers = await this.loadGiftBundleHelpers();
    if (giftHelpers?.appendBundleGiftItems) {
      items = await giftHelpers.appendBundleGiftItems(items);
    }

    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    });
    if (!response.ok) throw new Error("添加购物车失败");
    window.location.href = "/cart";
  }
}

/* --------------------------
 * 🎨 表现层(Presentation Layer)
 * -------------------------- */

// ⑦ 工具函数集合
class DOMUtils {
  constructor(root) {
    this.root = root;
  }

  qs(sel) {
    return this.root.querySelector(sel);
  }

  /** 获取当前 combo-save 区块的 class 前缀，用于兼容 valentine2026 / spring2026 */
  getComboSavePrefix() {
    const section = this.root.querySelector('[class*="combo-save"]');
    const blockClass = section?.className?.split(/\s+/).find((c) => c.includes("combo-save"));
    return blockClass || "valentine2026_combo-save";
  }

  qsa(sel) {
    return Array.from(this.root.querySelectorAll(sel));
  }

  /**
   * 根据屏幕宽度选择响应式元素
   */
  qsResponsive(selector, breakpoint = 720) {
    const elements = this.qsa(selector);
    if (elements.length === 0) return null;
    if (elements.length === 1) return elements[0];

    const viewportWidth = window.innerWidth;
    const index = viewportWidth > breakpoint ? 0 : 1;
    return elements[index] || elements[0];
  }

  static money(cents = 0) {
    if (typeof cents !== "number" || isNaN(cents)) return "$0.00";
    const sign = cents < 0 ? "-" : "";
    return `${sign}$${(Math.abs(cents) / 100).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  static moneyNoDecimals(cents = 0) {
    return (cents / 100).toLocaleString("en-US", {
      minimumFractionDigits: 0,
    });
  }

  static preloadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  static async swapImage(imgEl, newSrc) {
    if (!imgEl || !newSrc) return;
    if (imgEl.currentSrc === newSrc || imgEl.src === newSrc) return;

    try {
      await DOMUtils.preloadImage(newSrc);
      imgEl.src = newSrc;
    } catch (e) {
      console.warn("图片加载失败:", newSrc, e);
    }
  }
}

// ⑧ DOM缓存管理器
class DOMCache {
  constructor(domUtils) {
    this.cache = new Map();
    this.domUtils = domUtils;
  }

  get(key, selector) {
    if (!this.cache.has(key)) {
      const el = this.domUtils.qs(selector);
      if (el) this.cache.set(key, el);
    }
    return this.cache.get(key);
  }

  getSlotElements(slot) {
    const prefix = `slot-${slot}`;
    return {
      card: this.get(`${prefix}-card`, `[data-slot="${slot}"]`),
      image: this.get(
        `${prefix}-image`,
        `[data-slot="${slot}"] [class*="combo-save__product-image"]`,
      ),
      name: this.get(
        `${prefix}-name`,
        `[data-slot="${slot}"] [class*="combo-save__product-name"]`,
      ),
      price: this.get(
        `${prefix}-price`,
        `[data-slot="${slot}"] [class*="combo-save__price-current"]`,
      ),
      compare: this.get(
        `${prefix}-compare`,
        `[data-slot="${slot}"] [class*="combo-save__price-original"]`,
      ),
      dropdown: this.get(
        `${prefix}-dropdown`,
        `[data-slot="${slot}"] .bf-select-list`,
      ),
      label: this.get(
        `${prefix}-label`,
        `[data-slot="${slot}"] [class*="combo-save__tag-text"]`,
      ),
      trigger: this.get(
        `${prefix}-trigger`,
        `[data-slot="${slot}"] [class*="combo-save__product-tag"]`,
      ),
      link: this.get(
        `${prefix}-link`,
        `[data-slot="${slot}"] [class*="combo-save__product-content"]`,
      ),
    };
  }

  clear() {
    this.cache.clear();
  }
}

// ⑨ 纯渲染函数集合
class Renderers {
  constructor(domUtils) {
    this.domUtils = domUtils;
  }

  renderTiers(tiers, titleText) {
    const titleEl = this.domUtils.qs(".bf-tiers-title");
    const listEl = this.domUtils.qs("[data-tiers]");

    if (titleEl && titleText) titleEl.textContent = titleText;
    if (!listEl) return;

    const html = tiers
      .map(
        (t) => `
      <li class="bf-tier" data-over="${t.over}" data-off="${t.off}">
        $${DOMUtils.moneyNoDecimals(
          t.off,
        )} OFF Over $${DOMUtils.moneyNoDecimals(t.over)}
      </li>
    `,
      )
      .join("");

    listEl.innerHTML = html;
  }

  renderProductCard(elements, product, variant, config) {
    const { image, name, price, compare } = elements;

    // 渲染图片
    const imgUrl =
      variant?.featured_image?.src ||
      product.images?.[0] ||
      product.featured_image ||
      "";
    let imgEl = image?.querySelector("img");

    if (!imgEl && image) {
      imgEl = document.createElement("img");
      imgEl.alt = product.title || "";
      imgEl.loading = "eager";
      imgEl.decoding = "async";
      image.appendChild(imgEl);
    }

    if (imgEl && imgUrl) {
      imgEl.alt = product.title || "";
      const imageWidth = window.innerWidth > 1200 ? 800 : 600;
      DOMUtils.swapImage(imgEl, imgUrl + `&width=${imageWidth}`);
    }

    // 渲染标题
    let displayName = product.title || "";
    if (variant?.title && variant.title !== "Default Title") {
      displayName = `${product.title} - ${variant.title}`;
    }
    if (name) name.textContent = displayName;

    // 渲染价格
    const currentPrice = variant?.price ?? product.price ?? 0;
    const comparePrice =
      variant?.compare_at_price ?? product.compare_at_price ?? 0;

    if (price) price.textContent = DOMUtils.money(currentPrice);
    if (compare) {
      compare.textContent =
        comparePrice > currentPrice ? DOMUtils.money(comparePrice) : "";
    }
  }

  renderVariantSelector(elements, product, currentVariant, config) {
    const { card, image } = elements;
    if (!card) return;

    const prefix = this.domUtils.getComboSavePrefix();
    const selector = `.${prefix}__color-selector`;
    const existingSelector = card.querySelector(selector);

    // 不应该显示变体选择器时移除并返回
    if (
      !config.shouldShowVariantSelector(product.title) ||
      !product.variants ||
      product.variants.length <= 1
    ) {
      existingSelector?.remove();
      return;
    }

    // 获取或创建变体选择器
    const variantBox =
      existingSelector ||
      (() => {
        const box = document.createElement("div");
        box.className = `${prefix}__color-selector`;
        (image || card).appendChild(box);
        return box;
      })();

    // 生成颜色按钮
    const colorButtons = product.variants
      .filter((v) => v.available)
      .map((v, idx) => {
        const colorClass = config.getColorClass(v.title);
        const activeClass =
          v.id === currentVariant?.id ? `${prefix.split('_')[0]}_${colorClass}-active` : "";
        return `
        <span class="${prefix}__color-${colorClass} ${activeClass}" 
              data-variant-id="${v.id}" 
              data-variant-index="${idx}">
          <span class="${prefix}__color-${colorClass}-inner"></span>
        </span>`;
      })
      .join("");

    variantBox.innerHTML = `
      <span class="${prefix}__color-label">Color ：</span>
      ${colorButtons}
    `;
  }

  async renderDropdownOptions(slot, products, productService, config) {
    const dropdown = this.domUtils.qs(`[data-slot="${slot}"] .bf-select-list`);
    if (!dropdown) return;

    dropdown.innerHTML = "";
    const fragment = document.createDocumentFragment();

    for (const product of products) {
      // 展开变体的商品
      if (config.shouldExpandVariants(product.title)) {
        try {
          const fullProduct = await productService.fetchProduct(product.handle);
          if (fullProduct.variants?.length > 1) {
            fullProduct.variants
              .filter((v) => v.available)
              .forEach((variant) => {
                fragment.appendChild(
                  this._createOptionElement(fullProduct, variant),
                );
              });
            continue;
          }
        } catch (e) {
          console.warn(`Failed to load product variants: ${product.title}`, e);
        }
      }
      // 普通商品选项（仅当首个变体可用时添加）
      if (product.variants?.[0]?.available || product.variants?.[1]?.available) {
        fragment.appendChild(this._createOptionElement(product));
      }
    }

    dropdown.appendChild(fragment);
  }

  _createOptionElement(product, variant = null) {
    const li = document.createElement("li");
    li.className = "bf-select-option";
    li.setAttribute("role", "option");
    li.dataset.handle = product.handle;

    if (variant) {
      li.dataset.variantId = variant.id;
      li.innerHTML = `<span>${product.title} - ${variant.title}</span>`;
    } else {
      li.innerHTML = `<span>${product.title}</span>`;
    }

    return li;
  }

  setSelectedOption(slot, title) {
    const card = this.domUtils.qs(`[data-slot="${slot}"]`);
    if (!card) return;

    const allOptions = card.querySelectorAll(".bf-select-option");
    let foundFirst = false;
    for (const opt of allOptions) {
      const text = opt.textContent.trim();
      const isMatch =
        !foundFirst && (text === title || text.startsWith(title + " - "));
      opt.classList.toggle("selected", isMatch);
      if (isMatch) foundFirst = true;
    }
  }

  updateTotals(elements, totals, savings, achievedTier, nextTier, tierAmount) {
    const { discountedPrice, originalPrice, save, saveNote, hint } = elements;
    const variantDeduction = savings.variantDeduction || 0;

    const payableTotal = Math.max(
      0,
      totals.price - savings.extraOff - variantDeduction,
    );
    const displaySavings = Math.max(0, savings.totalSavings + variantDeduction);

    if (discountedPrice)
      discountedPrice.textContent = DOMUtils.money(payableTotal);
    if (originalPrice) originalPrice.textContent = DOMUtils.money(totals.compare);
    if (save)
      save.textContent = `$${DOMUtils.moneyNoDecimals(displaySavings)}`;

    // 更新节省说明
    if (saveNote) {
      const parts = [];
      if (savings.baseDiscount > 0) {
        parts.push(`$${DOMUtils.moneyNoDecimals(savings.baseDiscount)} OFF`);
      }
      if (savings.extraOff > 0) {
        parts.push(`EXTRA $${DOMUtils.moneyNoDecimals(savings.extraOff)}`);
      }
      saveNote.textContent = parts.length ? `(${parts.join(" + ")})` : "";
    }

    // 更新阶梯提示
    if (hint) {
      hint.textContent = this._getTierHintText(
        achievedTier,
        nextTier,
        tierAmount,
      );
    }
  }

  _getTierHintText(achieved, next, currentAmount) {
    if (!achieved && next) {
      const needed = next.over - currentAmount;
      return `Add ${DOMUtils.money(
        needed,
      )} more to unlock $${DOMUtils.moneyNoDecimals(next.off)} off.`;
    }

    if (achieved && next) {
      const needed = next.over - currentAmount;
      return `$${DOMUtils.moneyNoDecimals(
        achieved.off,
      )} unlocked - add ${DOMUtils.money(
        needed,
      )} more to unlock $${DOMUtils.moneyNoDecimals(next.off)} off.`;
    }

    if (achieved && !next) {
      return `$${DOMUtils.moneyNoDecimals(
        achieved.off,
      )} unlocked — Maximum savings achieved!`;
    }

    return "";
  }
}

// ⑩ UI控制器
class UIController {
  constructor(
    root,
    stateStore,
    productSelector,
    discountEngine,
    configService,
    checkoutCoordinator,
    productService,
  ) {
    this.root = root;
    this.store = stateStore;
    this.selector = productSelector;
    this.discount = discountEngine;
    this.config = configService;
    this.checkout = checkoutCoordinator;
    this.productService = productService;
    this.domUtils = new DOMUtils(root);
    this.domCache = new DOMCache(this.domUtils);
    this.renderers = new Renderers(this.domUtils);
    this._setupStateListeners();
  }

  _setupStateListeners() {
    this.store.subscribe(({ type, payload }) => {
      if (type === "PRODUCT_SELECTED") {
        this._onProductSelected(payload);
      } else if (type === "VARIANT_CHANGED") {
        this._onVariantChanged(payload);
      }
    });
  }

  _onProductSelected({ slot, product, variant }) {
    const elements = this.domCache.getSlotElements(slot);
    this.renderers.renderProductCard(elements, product, variant, this.config);
    this.renderers.renderVariantSelector(
      elements,
      product,
      variant,
      this.config,
    );
    this._updateProductLink(elements, product, variant);
    this._updateTotals();
  }

  _onVariantChanged({ slot, variant }) {
    const { product } = this.store.getSelection(slot);
    const elements = this.domCache.getSlotElements(slot);
    this.renderers.renderProductCard(elements, product, variant, this.config);
    this.renderers.renderVariantSelector(
      elements,
      product,
      variant,
      this.config,
    );
    this._updateProductLink(elements, product, variant);
    this._updateTotals();
  }

  _updateProductLink(elements, product, variant) {
    if (elements.link && product?.handle) {
      let url = `https://us.robosen.com/products/${product.handle}`;
      if (variant?.id) {
        url += `?variant=${variant.id}`;
      }
      
      // 移除旧的事件监听器
      const oldHandler = elements.link._clickHandler;
      if (oldHandler) {
        elements.link.removeEventListener('click', oldHandler);
      }
      
      // 创建新的事件处理函数
      const clickHandler = (e) => {
        // 双重检查：检查点击目标是否在颜色选择器或下拉菜单内
        if (e.target.closest('[class*="combo-save__color-selector"]') || 
            e.target.closest('[class*="combo-save__product-tag"]')) {
          return; // 不执行跳转
        }
        
        e.preventDefault();
        window.open(url, '_blank');
      };
      
      // 保存事件处理函数引用以便后续移除
      elements.link._clickHandler = clickHandler;
      elements.link.addEventListener('click', clickHandler);
    }
  }

  _updateTotals() {
    const totals = this.discount.calculateTotals();
    const tierAmount = this.discount.calculateTierEligibleAmount(totals.price);
    const achievedTier = this.discount.getAchievedTier(tierAmount);
    const nextTier = this.discount.getNextTier(tierAmount);

    const extraOff = achievedTier?.off || 0;
    const baseDiscount = Math.max(totals.compare - totals.price, 0);
    const totalSavings = baseDiscount + extraOff;
    const variantDeduction = this.discount.calculateVariantDeduction();

    const elements = {
      discountedPrice: this.domUtils.qs("[data-total]"),
      originalPrice: this.domUtils.qs("[data-original-price]"),
      save: this.domUtils.qs("[data-save]"),
      saveNote: this.domUtils.qs("[data-save-note]"),
      hint: this.domUtils.qsResponsive("[data-tier-hint]", 720),
    };
    this.renderers.updateTotals(
      elements,
      totals,
      { extraOff, baseDiscount, totalSavings, variantDeduction },
      achievedTier,
      nextTier,
      tierAmount,
    );

    // 进度条与满减门槛一致；两边都是 exclude-titles 时视觉填满
    this._updateProgressBar(tierAmount);

    // 根据 variantDeduction 控制 total-note 的显示/隐藏
    const totalNote = this.domUtils.qs(".spring2026_combo-save__total-note");
    if (totalNote) {
      totalNote.style.display = variantDeduction > 0 ? "" : "none";
    }

    // 检查是否需要隐藏解锁提示
    this._toggleUnlockMessage();
  }

  /**
   * 两个插槽是否都选中了 exclude-titles 中的商品
   */
  _areBothSelectionsExcluded() {
    const selections = this.store.getAllSelections();
    const excludedTitles = this.discount?.excludedTitles;
    if (!selections.length || !excludedTitles?.size) return false;

    return selections.every(({ product }) => {
      if (!product) return false;
      const title = (product.title || "").toLowerCase();
      return excludedTitles.has(title);
    });
  }

  /**
   * 更新进度条状态
   * @param {number} eligibleAmount - 满减资格金额(单位:分)，已排除 exclude-titles 商品
   */
  _updateProgressBar(eligibleAmount) {
    const tiers = this.discount?.getTiers?.() || [];
    const lastTier = tiers.length ? tiers[tiers.length - 1] : null;
    // 目标金额：当前配置最高满减门槛；无 tiers 时兜底 $1458
    const TARGET_AMOUNT = lastTier?.over || 145800;

    // 计算进度比例,限制在 0-100% 之间
    let percentage =
      Math.min(Math.max(eligibleAmount / TARGET_AMOUNT, 0), 1) * 100;

    // 两边都是排除商品时无法计入满减，进度条仍填满以提示「已达特殊组合态」
    if (this._areBothSelectionsExcluded()) {
      percentage = 100;
    }

    // 获取进度条元素 (PC端第一个，移动端第二个)
    const progressBar = this.domUtils.qsResponsive('.promotion-progress', 720);
    const progressPoint = this.domUtils.qsResponsive('.promotion-progress-point-box', 720);

    if (progressBar) {
      // 设置进度条宽度,添加过渡效果
      progressBar.style.transition = 'width 0.7s cubic-bezier(0.4, 0, 0.2, 1)';
      progressBar.style.width = `${percentage}%`;
    }

    if (progressPoint) {
      // 设置进度点位置,添加过渡效果
      progressPoint.style.transition = 'left 0.7s cubic-bezier(0.4, 0, 0.2, 1)';
      progressPoint.style.left = `${percentage}%`;
    }
  }

  async applyDefaultSelections() {
    const title1 = this.config.get("product1Title");
    const title2 = this.config.get("product2Title");

    const products = this.store.getProducts();

    // 查找产品并解析可能的变体后缀
    const result1 = await this._findProductAndVariant(products, title1);
    const result2 = await this._findProductAndVariant(products, title2);

    if (result1?.product && result2?.product) {
      await Promise.all([
        this.selector.selectProduct(1, result1.product.handle, result1.variantId),
        this.selector.selectProduct(2, result2.product.handle, result2.variantId),
      ]);

      // 更新下拉标签
      const label1 = this.domCache.get(
        "label-1",
        '[data-slot="1"] [class*="combo-save__tag-text"]',
      );
      const label2 = this.domCache.get(
        "label-2",
        '[data-slot="2"] [class*="combo-save__tag-text"]',
      );
      if (label1) label1.textContent = title1;
      if (label2) label2.textContent = title2;

      // 设置选中状态
      this.renderers.setSelectedOption(1, title1);
      this.renderers.setSelectedOption(2, title2);
    }
  }

  /**
   * 查找产品和变体
   * 支持格式：
   * - "Product Title" (完整匹配)
   * - "Product Title - Variant Title" (产品 + 变体)
   */
  async _findProductAndVariant(products, titleStr) {
    // 尝试精确匹配产品标题
    let product = products.find((p) => p.title === titleStr);
    if (product) {
      return { product, variantId: null };
    }

    // 尝试分割标题，解析产品名和变体名
    const parts = titleStr.split(" - ");
    if (parts.length >= 2) {
      const productTitle = parts[0].trim();
      const variantTitle = parts.slice(1).join(" - ").trim();

      product = products.find((p) => p.title === productTitle);
      if (product) {
        // 获取完整的产品数据以访问变体
        try {
          const fullProduct = await this.productService.fetchProduct(product.handle);
          const variant = fullProduct.variants?.find(
            (v) => v.title === variantTitle && v.available
          );
          
          if (variant) {
            return { product, variantId: variant.id };
          }
        } catch (e) {
          console.warn(`Failed to load variants for: ${productTitle}`, e);
        }

        // 如果没找到匹配的变体，返回产品但不指定变体
        return { product, variantId: null };
      }
    }

    return null;
  }

  bindEvents() {
    this._bindDropdownEvents();
    this._bindVariantEvents();
    this._bindCheckoutEvent();
  }

  _bindDropdownEvents() {
    const productsRoot =
      this.domUtils.qs("#combo-save-products") ||
      this.domUtils.qs("#christmas-combo-save-products") ||
      this.domUtils.qs('[class*="combo-save__products"]');
    if (!productsRoot) return;

    productsRoot.addEventListener("click", async (e) => {
      const trigger = e.target.closest('[class*="combo-save__product-tag"]');
      if (trigger && !e.target.closest(".bf-select-option")) {
        this._toggleDropdown(trigger);
      }

      const option = e.target.closest(".bf-select-option");
      if (option) {
        await this._handleOptionSelect(option);
      }
    });

    // 全局关闭(限制在组件内部)
    this.root.addEventListener("click", (e) => {
      if (e.target.closest('[class*="combo-save__product-tag"]')) return;
      this.root
        .querySelectorAll('[class*="combo-save__product-tag"].open')
        .forEach((s) => {
          s.classList.remove("open");
          s.setAttribute("aria-expanded", "false");
        });
    });
  }

  _toggleDropdown(trigger) {
    const isOpen = trigger.classList.contains("open");

    // 关闭所有其他下拉
    this.root
      .querySelectorAll('[class*="combo-save__product-tag"].open')
      .forEach((s) => {
        if (s !== trigger) {
          s.classList.remove("open");
          s.setAttribute("aria-expanded", "false");
        }
      });

    // 切换当前下拉
    trigger.classList.toggle("open");
    trigger.setAttribute("aria-expanded", String(!isOpen));
  }

  async _handleOptionSelect(option) {
    const card = option.closest('[class*="combo-save__product-card"]');
    const slot = Number(card?.dataset.slot);
    if (!slot) return;

    const handle = option.dataset.handle;
    const variantId = option.dataset.variantId;
    const optionText = option.textContent.trim();

    // 更新下拉标签
    const elements = this.domCache.getSlotElements(slot);
    if (elements.label) elements.label.textContent = optionText;

    // 设置选中状态
    const allOptions = card.querySelectorAll(".bf-select-option");
    allOptions.forEach((opt) => opt.classList.remove("selected"));
    option.classList.add("selected");

    // 关闭下拉
    if (elements.trigger) {
      elements.trigger.classList.remove("open");
      elements.trigger.setAttribute("aria-expanded", "false");
    }

    // 选择商品
    try {
      await this.selector.selectProduct(
        slot,
        handle,
        variantId ? Number(variantId) : null,
      );

      // 检查是否需要隐藏解锁提示
      this._toggleUnlockMessage();
    } catch (error) {
      console.error("Failed to select product:", error);
      alert("Failed to load product, please try again");
    }
  }

  _bindVariantEvents() {
    const productsRoot = this.domUtils.qs('[class*="combo-save__products"]');
    if (!productsRoot) return;

    productsRoot.addEventListener("click", (e) => {
      const variantButton = e.target.closest("[data-variant-id]");
      if (!variantButton) return;

      const card = variantButton.closest(
        '[class*="combo-save__product-card"]',
      );
      const slot = Number(card?.dataset.slot);
      if (!slot) return;

      const variantId = Number(variantButton.dataset.variantId);
      this.selector.changeVariant(slot, variantId);
    });
  }

  _bindCheckoutEvent() {
    const checkoutBtn = this.domUtils.qs("[data-checkout]");
    if (!checkoutBtn) return;

    checkoutBtn.addEventListener("click", async () => {
      try {
        await this.checkout.checkout();
      } catch (error) {
        alert(error.message || "Checkout failed, please try again");
      }
    });
  }

  /**
   * 切换解锁提示的显示/隐藏状态
   * 当两个插槽都选择了排除商品时,隐藏解锁提示
   */
  _toggleUnlockMessage() {
    const unlockMessage = this.domUtils.qsResponsive(".extra-discount", 720);
    if (!unlockMessage) return;

    unlockMessage.style.display = this._areBothSelectionsExcluded()
      ? "none"
      : "";
  }

  destroy() {
    this.domCache.clear();
  }
}

/* --------------------------
 * 🚀 应用主类(Application)
 * -------------------------- */
class ComboSaveApp {
  constructor(root, config = {}) {
    this.root = root;

    // 数据层
    this.configService = new ConfigService(config);
    this.productService = new ProductService();
    this.stateStore = new StateStore();

    // 业务逻辑层
    this.discountEngine = new DiscountEngine(
      this.configService,
      this.stateStore,
    );
    this.productSelector = new ProductSelector(
      this.productService,
      this.stateStore,
      this.configService,
      this.discountEngine,
    );
    this.checkoutCoordinator = new CheckoutCoordinator(this.productSelector);

    // 表现层
    this.uiController = new UIController(
      root,
      this.stateStore,
      this.productSelector,
      this.discountEngine,
      this.configService,
      this.checkoutCoordinator,
      this.productService,
    );
  }

  async init() {
    try {
      // 2. 解析并渲染阶梯满减规则
      const tiersConfig = this.configService.get("tiers");
      const tiersTitle = this.configService.get("tiersTitle");
      this.discountEngine.parseTiers(tiersConfig);
      this.uiController.renderers.renderTiers(
        this.discountEngine.getTiers(),
        tiersTitle,
      );

      // 3. 设置排除商品
      const excludeTitles = this.configService.get("excludeTitles");
      this.discountEngine.setExcludedTitles(excludeTitles);

      // 4. 加载商品列表
      const collectionHandle = this.configService.get("collectionHandle");
      if (!collectionHandle) {
        console.warn("未配置产品集合");
        return;
      }

      const limit = this.configService.get("limit", 100);
      const products = await this.productSelector.loadProductList(
        collectionHandle,
        limit,
      );

      if (products.length === 0) {
        console.warn("产品集合为空");
        return;
      }

      // 5. 渲染下拉选项(并发处理)
      await Promise.all([
        this.uiController.renderers.renderDropdownOptions(
          1,
          products,
          this.productService,
          this.configService,
        ),
        this.uiController.renderers.renderDropdownOptions(
          2,
          products,
          this.productService,
          this.configService,
        ),
      ]);

      // 6. 应用默认选择
      await this.uiController.applyDefaultSelections();

      // 7. 绑定所有事件
      this.uiController.bindEvents();
    } catch (error) {
      console.error("❌ Combo Save 组件初始化失败:", error);
      const errorEl = this.root.querySelector(".bf-error");
      if (errorEl) {
        errorEl.textContent = "Failed to load page, please refresh";
      }
    }
  }

  destroy() {
    this.uiController.destroy();
    this.productService.clear();
    this.stateStore.clear();
  }
}

/* --------------------------
 * 🎯 Web Component 定义
 * -------------------------- */
class ComboSaveElement extends HTMLElement {
  static get observedAttributes() {
    return [
      "collection-handle",
      "product1-title",
      "product2-title",
      "tiers",
      "tiers-title",
      "exclude-titles",
      "limit",
    ];
  }

  constructor() {
    super();
    this.app = null;
  }

  connectedCallback() {
    this._initialize();
  }

  disconnectedCallback() {
    this._cleanup();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    // 属性变化时重新初始化(仅在已初始化的情况下)
    if (oldValue !== newValue && this.app) {
      this._cleanup();
      this._initialize();
    }
  }

  _initialize() {
    const config = this._parseConfig();
    this.app = new ComboSaveApp(this, config);
    this.app.init();
  }

  _cleanup() {
    if (this.app) {
      this.app.destroy();
      this.app = null;
    }
  }

  _parseConfig() {
    return {
      collectionHandle: this.getAttribute("collection-handle") || "",
      product1Title: this.getAttribute("product1-title") || "",
      product2Title: this.getAttribute("product2-title") || "",
      tiers: this.getAttribute("tiers") || "",
      tiersTitle: this.getAttribute("tiers-title") || "",
      excludeTitles: this.getAttribute("exclude-titles") || "",
      limit: parseInt(this.getAttribute("limit")) || 100,
    };
  }

  // 公共API:动态更新配置
  updateConfig(newConfig) {
    Object.entries(newConfig).forEach(([key, value]) => {
      const attrName = key.replace(/([A-Z])/g, "-$1").toLowerCase();
      this.setAttribute(attrName, String(value));
    });
  }
}

// 注册自定义元素
if (!customElements.get("combo-save")) {
  customElements.define("combo-save", ComboSaveElement);
}
